import { useState } from "react";
import { motion } from "framer-motion";
import { Section } from "./Section";
import { Reveal } from "./Reveal";
import { COMPARISON_RAW } from "../../config/site";
import { CheckCircle2, XCircle } from "lucide-react";

const MAX_HIGH = Math.max(...COMPARISON_RAW.map((r) => r.high));
const TOTAL_LOW = COMPARISON_RAW.reduce((s, r) => s + r.low, 0);
const TOTAL_HIGH = COMPARISON_RAW.reduce((s, r) => s + r.high, 0);

const NOTES = {
  "Land levelling": "Weeks of earthwork before anything else can start.",
  "Boundary wall": "Your cost, your contractor, your follow-up.",
  "Road access": "Approach roads are your problem on raw land.",
  "Electricity connection": "Industrial load sanctions take months.",
  "STP / ETP": "Mandatory for most manufacturing — and expensive alone.",
  "Labour quarters": "Without housing nearby, workers don't stay.",
  "NOCs & approvals": "CLU, pollution, fire, factory licence — each a queue.",
};

export const Comparison = () => {
  const [active, setActive] = useState(null);

  return (
    <Section
      id="comparison"
      overline="The real math"
      title="Raw land vs a BIDC plot"
      sub="A ½ acre of agricultural land looks cheaper — until you price everything it doesn't come with. Tap any row."
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <Reveal>
          <div className="h-full border border-border bg-paper-light p-6 sm:p-8" data-testid="comparison-raw">
            <h3 className="font-display text-2xl font-medium text-forest">The raw land route</h3>
            <div className="mt-6 space-y-1">
              {COMPARISON_RAW.map((r) => (
                <button
                  key={r.item}
                  onClick={() => setActive(active === r.item ? null : r.item)}
                  data-testid={`raw-item-${r.item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`block w-full px-3 py-2.5 text-left transition-colors duration-150 ${
                    active === r.item ? "bg-sand/70" : "hover:bg-sand/40"
                  }`}
                >
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-ink">{r.item}</span>
                    <span className="text-[#4A4C4A]">
                      {r.low === r.high ? `₹${r.low} L` : `₹${r.low}–${r.high} L`}
                    </span>
                  </div>
                  <div className="mt-1.5 h-1.5 w-full bg-border/70">
                    <motion.div
                      className="h-full bg-clay/80"
                      initial={{ width: 0 }}
                      whileInView={{ width: `${(r.high / MAX_HIGH) * 100}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                    />
                  </div>
                  {active === r.item && NOTES[r.item] && (
                    <p className="mt-2 text-xs italic text-clay">{NOTES[r.item]}</p>
                  )}
                </button>
              ))}
            </div>
            <div className="mt-5 flex items-center justify-between border-t-2 border-forest pt-4">
              <span className="flex items-center gap-2 text-sm font-medium text-ink">
                <XCircle className="h-4 w-4 text-clay" /> True cost of raw land
              </span>
              <span className="font-display text-xl font-semibold text-clay" data-testid="raw-total">
                ₹{TOTAL_LOW / 100 >= 1 ? `${(TOTAL_LOW / 100).toFixed(2)} Cr` : `${TOTAL_LOW} L`} – ₹
                {(TOTAL_HIGH / 100).toFixed(2)} Cr
              </span>
            </div>
            <p className="mt-2 text-xs text-clay/90">+ months of delays before construction can even begin.</p>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="flex h-full flex-col bg-forest p-6 sm:p-8 grain relative" data-testid="comparison-bidc">
            <div className="relative flex h-full flex-col">
              <h3 className="font-display text-2xl font-medium text-paper">The BIDC plot</h3>
              <p className="mt-6 font-display text-5xl sm:text-6xl font-medium text-gold">
                ₹1 Cr<span className="align-top text-2xl">*</span>
              </p>
              <p className="mt-2 text-xs uppercase tracking-[0.18em] text-paper/50">
                * Tentative pricing
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  "Levelled, approved industrial land",
                  "60.5 ft RCC roads to your gate",
                  "24×7 industrial power feeder",
                  "CETP + STP already inside",
                  "Fire station, CCTV, boundary, gate",
                  "Labour quarters & ecosystem",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-sm text-paper/85">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-gold" /> {t}
                  </li>
                ))}
              </ul>
              <p className="mt-auto pt-8 font-display text-lg italic text-gold/90">
                Same money. Zero delays. A factory address, not a land file.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </Section>
  );
};
